import './governanceDialog.scss';
import { Grid, Typography, Button, IconButton } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { GovernanceStatusData, GovernanceStatusEnum, RoomStatusData, RoomStatusEnum } from '../../../data/Enums';
import { useCallback, useEffect, useMemo } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import FormHelper from '../../../helpers/FormHelper';
import DefaultConstants from '../../../data/Constants';
import { usePartnerPmsContext } from '../../../contexts/PartnerPmsContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { AttachFile, Description, DryCleaning, Fastfood, HomeRepairService, InfoOutlined, PendingActions, WhereToVote } from '@mui/icons-material';
import StringHelper from '../../../helpers/StringHelper';
import useDialog from '../../../hooks/useDialog';
import RoomInformationsDialog from './RoomInformationsDialog';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import ReservationInformationsDialog from './ReservationInformationsDialog';
import DoNotDisturbConfirmationDialog from './DoNotDisturbConfirmationDialog';
import FormOption from '../../FormOption';
import PartnerPmsService from '../../../services/PartnerPmsService';
import RoomServiceItemsListDialog from './RoomServiceItemsListDialog';
import GovernanceItemsDialog from './GovernanceItemsDialog';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import TextField from '../../TextField';
import AddRoomBlockingDialog from './AddRoomBlockingDialog';
import RoomBlockingInformationsDialog from './RoomBlockingInformationsDialog';
import useState from '../../../hooks/useState';
import ViewServiceOrdersDialog from './ViewServiceOrdersDialog';
import LogsDialog from './LogsDialog';
import AddAttachmentsDialog from '../AddAttachmentsDialog';
import Badge from '../../Badge';


const governanceStatusInspection = GovernanceStatusData[GovernanceStatusEnum.Inspection];

const roomBlocking = RoomStatusData[RoomStatusEnum.RoomBlocking];
const ownerReservation = RoomStatusData[RoomStatusEnum.OwnerReservation];
const vipReservation = RoomStatusData[RoomStatusEnum.VipReservation];
const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];
const expectedCheckIn = RoomStatusData[RoomStatusEnum.ExpectedCheckIn];
const expectedCheckOut = RoomStatusData[RoomStatusEnum.ExpectedCheckOut];

interface IGovernanceDialogProps extends IDefaultDialogProps {
    roomId?: number;
}

interface IStateValues {
    room: IRoom | undefined;
    governanceStatusId: string | undefined;
    observations: string;
    reservationKey: 'checkInReservation' | 'expectedReservation' | undefined;
    hasPendingGovernanceItems: boolean;
    attachments: File[];
}


const GovernanceDialog: React.FC<IGovernanceDialogProps> = ({
    open,
    handleClose,
    roomId = undefined
}) => {
    const { translate } = useLanguageContext();
    const { showToast } = useNotificationContext();
    const { handleRefreshRooms } = usePartnerPmsContext();
    const { enterprise, hasPermission } = useAuthContext();

    const state = useState<IStateValues>({
        room: undefined,
        governanceStatusId: undefined,
        observations: '',
        reservationKey: undefined,
        hasPendingGovernanceItems: false,
        attachments: []
    });

    const roomInformationsDialog = useDialog();
    const roomServiceItemsListDialog = useDialog();
    const doNotDisturbConfirmationDialog = useDialog();
    const addRoomBlockingDialog = useDialog();
    const roomBlockingInformationsDialog = useDialog();
    const governanceItemsDialog = useDialog();
    const viewServiceOrdersDialog = useDialog();
    const logsDialog = useDialog();
    const addAttachmentsDialog = useDialog();
    

    useEffect(() => {
        if (roomId !== undefined) {
            state.reset();
            handleRefreshSelectedRoom();

            roomInformationsDialog.handleClose();
            roomServiceItemsListDialog.handleClose();
            doNotDisturbConfirmationDialog.handleClose();
            addRoomBlockingDialog.handleClose();
            roomBlockingInformationsDialog.handleClose();
            governanceItemsDialog.handleClose();
            viewServiceOrdersDialog.handleClose();
            logsDialog.handleClose();
            addAttachmentsDialog.handleClose();
        }
        // eslint-disable-next-line
    }, [roomId]);


    const handleRefreshSelectedRoom = useCallback(() => {
        handleRefreshRooms((rooms) => {
            state.update('room', rooms.find(r => r.id === roomId));
        });
        // eslint-disable-next-line
    }, [roomId]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (state.values.room === undefined) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'roomId', state.values.room.id);

        state.values.attachments.forEach((attachment, index) => {
            formData = FormHelper.Add(formData, `attachment[${index}]`, attachment);
        });

        PartnerPmsService.UpdateRoomGovernanceStatus(formData).then(() => {
            handleClose();
        });
        // eslint-disable-next-line
    }, [state.values.room, state.values.attachments]);


    // Permissions
    const editGovernanceStatus = useMemo(() => {
        return hasPermission(`Governance.RoomOverview.EditGovernanceStatus`);
        // eslint-disable-next-line
    }, []);

    const editRoomObservations = useMemo(() => {
        return hasPermission(`Governance.RoomOverview.EditRoomObservations`);
        // eslint-disable-next-line
    }, []);

    const launchRoomServiceItems = useMemo(() => {
        if (enterprise === undefined) {
            return false;
        }
        
        const roomServicePointOfSaleId = enterprise.roomServicePointOfSaleId ?? '';
        if (roomServicePointOfSaleId.trim().length === 0) {
            return false;
        }

        return hasPermission(`Governance.RoomOverview.LaunchRoomServiceItems`);
        // eslint-disable-next-line
    }, [enterprise]);

    const editDoNotDisturbStatus = useMemo(() => {
        return hasPermission(`Governance.RoomOverview.EditDoNotDisturbStatus`);
        // eslint-disable-next-line
    }, []);

    const addRoomBlocking = useMemo(() => {
        if (state.values.room === undefined) {
            return false;
        }
        
        const roomKey = (roomBlocking.partnerId ?? '') as keyof(IRoom);
        return hasPermission(`Governance.RoomOverview.RoomBlocking.Add`) && state.values.room[roomKey] !== true;
        // eslint-disable-next-line
    }, [state.values.room]);

    const governanceItems = useMemo(() => {
        const room = state.values.room;
        const governanceStatusId = state.values.governanceStatusId;

        if (enterprise === undefined || room === undefined) {
            return [];
        }

        if (room.governanceStatusId !== governanceStatusInspection.partnerId && (governanceStatusId === undefined || !enterprise.governanceStatusAllowedToDisplayGovernanceItems.includes(governanceStatusId))) {
            return [];
        }
        
        const roomTypeId = room.roomTypeId ?? 0;
        return enterprise.governanceItems.filter(i => i.roomTypes.length === 0 || i.roomTypes.includes(+roomTypeId)) ?? [];
    }, [enterprise, state.values.room, state.values.governanceStatusId]);

    const viewServiceOrders = useMemo(() => {
        return hasPermission(`Governance.RoomOverview.ServiceOrder.View`) || hasPermission(`Governance.RoomOverview.ServiceOrder.Add`);
        // eslint-disable-next-line
    }, []);


    const contentActionButtons = useMemo(() => {
        const room = state.values.room;
        const elements = [] as JSX.Element[];

        if (room) {
            // Room informations
            elements.push(<Button 
                className='action-button'  
                onClick={() => roomInformationsDialog.handleOpen()}
                key='roomInformation'
            >
                <InfoOutlined />
                <Typography>{translate('roomInformation')}</Typography>
            </Button>);
            
            // Governance items
            if (governanceItems.length > 0) {
                const actionButtonProps = {
                    Icon: DryCleaning,
                    name: 'governanceItems'
                };

                if (room.governanceStatusId === governanceStatusInspection.partnerId) {
                    actionButtonProps.Icon = PendingActions;
                    actionButtonProps.name = 'housekeepingDetails';
                }
                
                elements.push(<Button 
                    className='action-button'  
                    onClick={() => governanceItemsDialog.handleOpen()}
                    key='governanceItems'
                >
                    <actionButtonProps.Icon />
                    <Typography>{translate(actionButtonProps.name)}</Typography>
                </Button>);
            }
            
            // If exists checkin reservation
            if (room.checkInReservation) {
                const checkInReservation = room.checkInReservation; 
    
                // Vip guest
                if (checkInReservation.ownerGuestType) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${ownerReservation.color} !important` }} 
                        onClick={() => state.update('reservationKey', 'checkInReservation')}
                        key={ownerReservation.key}
                    >
                        <ownerReservation.Icon />
                        <Typography>{translate(ownerReservation.name)}</Typography>
                    </Button>);
                }
                
                // Vip guest
                else if (checkInReservation.vipGuestType) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${vipReservation.color} !important` }} 
                        onClick={() => state.update('reservationKey', 'checkInReservation')}
                        key={vipReservation.key}
                    >
                        <vipReservation.Icon />
                        <Typography>{translate(vipReservation.name)}</Typography>
                    </Button>);
                } 
                
                // Expected checkout
                else if (DateTimeHelper.Equals(checkInReservation.checkOutDate ?? '', DateTimeHelper.Now())) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${expectedCheckOut.color} !important` }} 
                        onClick={() => state.update('reservationKey', 'checkInReservation')}
                        key={expectedCheckOut.key}
                    >
                        <expectedCheckOut.Icon />
                        <Typography>{translate(expectedCheckOut.name)}</Typography>
                    </Button>);
                } 
                
                // Default
                else {
                    elements.push(<Button 
                        className='action-button' 
                        onClick={() => state.update('reservationKey', 'checkInReservation')}
                        key='default'
                    >
                        <WhereToVote />
                        <Typography>{translate('checkInReservation')}</Typography>
                    </Button>);
                }

                // Launch room service items
                if (launchRoomServiceItems) {
                    elements.push(<Button 
                        className='action-button' 
                        onClick={() => roomServiceItemsListDialog.handleOpen()}
                        key='launchItems'
                    >
                        <Fastfood />
                        <Typography>{translate('launchItems')}</Typography>
                    </Button>);
                }
            }
    
            // Expected checkin
            if (room.expectedCheckIn && room.expectedReservation) {
                elements.push(<Button 
                    className='action-button' 
                    sx={{ color: `${expectedCheckIn.color} !important` }} 
                    onClick={() => state.update('reservationKey', 'expectedReservation')}
                    key={expectedCheckIn.key}
                >
                    <expectedCheckIn.Icon />
                    <Typography>{translate(expectedCheckIn.name)}</Typography>
                </Button>);
            }
    
            // Do not disturb
            if (editDoNotDisturbStatus) {
                let doNotDisturbPrefixTitle = 'enable';
                let doNotDisturbColor = '#bdbdbd';
        
                if (room.doNotDisturb) {
                    doNotDisturbPrefixTitle = 'disable';
                    doNotDisturbColor = doNotDisturb.color;
                }
                
                elements.push(<Button 
                    className='action-button' 
                    sx={{ color: `${doNotDisturbColor} !important` }}
                    onClick={() => doNotDisturbConfirmationDialog.handleOpen()}
                    key={doNotDisturb.key}
                >
                    <doNotDisturb.Icon />
                    <Typography>{`${translate(doNotDisturbPrefixTitle)} "${translate(doNotDisturb.name).toLocaleLowerCase()}"`}</Typography>
                </Button>);
            }


            // Room blocking informations
            const roomBlockingKey = (roomBlocking.partnerId ?? '') as keyof(IRoom);
            if (room[roomBlockingKey] === true) {
                elements.push(<Button 
                    className='action-button'  
                    sx={{ color: `${roomBlocking.color} !important` }}
                    onClick={() => roomBlockingInformationsDialog.handleOpen()}
                    key='roomBlockingInformation'
                >
                    <roomBlocking.Icon />
                    <Typography>{translate('blockingInformation')}</Typography>
                </Button>);
            } 
            
            // Add room blocking
            else if (addRoomBlocking) {
                elements.push(<Button 
                    className='action-button'  
                    sx={{ color: `${roomBlocking.color} !important` }}
                    onClick={() => addRoomBlockingDialog.handleOpen()}
                    key={roomBlocking.key}
                >
                    <roomBlocking.Icon />
                    <Typography>{translate('blockRoom')}</Typography>
                </Button>);
            }


            // View service orders
            if (viewServiceOrders) {
                elements.push(<Button 
                    className='action-button'  
                    // sx={{ color: `${roomBlocking.color} !important` }}
                    onClick={() => viewServiceOrdersDialog.handleOpen()}
                    key='serviceOrderList'
                >
                    <HomeRepairService />
                    <Typography>{translate('serviceOrders')}</Typography>
                    <Typography className='indicator-text'>{room.serviceOrders.length}</Typography>
                </Button>);
            }


            // Log
            if (hasPermission(`Governance.Reports.RoomLog`)) {
                elements.push(<Button 
                    className='action-button color-blue-grey' 
                    onClick={() => logsDialog.handleOpen()}
                    key='changeLog'
                >
                    <Description />
                    <Typography>{translate('changeLog')}</Typography>
                    <Typography className='indicator-text backgroundcolor-blue-grey'>{room.logs?.length ?? 0}</Typography>
                </Button>);
            }
        }

        return elements;
        // eslint-disable-next-line
    }, [state.values.room, enterprise, launchRoomServiceItems, addRoomBlocking, governanceItems, viewServiceOrders, translate]);


    const dialogActions = useMemo(() => {
        const elements = [] as JSX.Element[];
        elements.push(<ActionButton action='cancel' onClick={handleClose} />);

        if (editGovernanceStatus) {
            const governanceItemsBlockSubmit = governanceItems.length > 0 && state.values.hasPendingGovernanceItems;
            elements.push(<ActionButton 
                action='save' 
                type={governanceItemsBlockSubmit ? 'button' : 'submit'} 
                onClick={() => {
                    if (governanceItemsBlockSubmit) {
                        governanceItemsDialog.handleOpen();
                        showToast('warning', translate('_governanceDialogGovernanceItemsPendingMarking'));
                    }
                }} 
            />);
        }

        return elements;
        // eslint-disable-next-line
    }, [translate, editGovernanceStatus, governanceItems, state.values.hasPendingGovernanceItems]);


    if (state.values.room === undefined) {
        return null;
    }
    
    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={`${translate('room')} ${state.values.room.description}`}
            dialogActions={dialogActions}
            fullWidth={true}
            fullHeight={true}
            maxWidth='sm'
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween overflow-inherit governancedialog-container'
            afterForm={state.values.room && <>
                <RoomInformationsDialog
                    open={roomInformationsDialog.open}
                    handleClose={() => roomInformationsDialog.handleClose()}
                    room={state.values.room}
                    handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                    editRoomObservations={editRoomObservations}
                />
    
                {
                    (state.values.room.checkInReservation || state.values.room.expectedReservation) && (
                        <ReservationInformationsDialog 
                            open={state.values.reservationKey !== undefined}
                            handleClose={() => state.update('reservationKey', undefined)}
                            reservation={state.values.reservationKey ? state.values.room[state.values.reservationKey] : undefined}
                            isCheckIn={state.values.reservationKey === 'checkInReservation'}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                {
                    launchRoomServiceItems && state.values.room.checkInReservation && (
                        <RoomServiceItemsListDialog 
                            open={roomServiceItemsListDialog.open}
                            handleClose={() => roomServiceItemsListDialog.handleClose()}
                            room={state.values.room}
                            reservation={state.values.room.checkInReservation}
                        />
                    )
                }
    
                {
                    editDoNotDisturbStatus && (
                        <DoNotDisturbConfirmationDialog 
                            open={doNotDisturbConfirmationDialog.open}
                            handleClose={() => doNotDisturbConfirmationDialog.handleClose()}
                            room={state.values.room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }
    
                {
                    addRoomBlocking && (
                        <AddRoomBlockingDialog 
                            open={addRoomBlockingDialog.open}
                            handleClose={() => addRoomBlockingDialog.handleClose()}
                            room={state.values.room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                {
                    roomBlocking.partnerId && state.values.room[roomBlocking.partnerId as keyof(IRoom)] === true && (
                        <RoomBlockingInformationsDialog 
                            open={roomBlockingInformationsDialog.open}
                            handleClose={() => roomBlockingInformationsDialog.handleClose()}
                            room={state.values.room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                {
                    viewServiceOrders && (
                        <ViewServiceOrdersDialog 
                            open={viewServiceOrdersDialog.open}
                            handleClose={() => viewServiceOrdersDialog.handleClose()}
                            room={state.values.room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                <LogsDialog
                    open={logsDialog.open}
                    handleClose={() => logsDialog.handleClose()}
                    room={state.values.room}
                />
            </>}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                {
                    contentActionButtons.length > 0 && <Grid container className='actions-container' marginTop={3}>
                        {contentActionButtons}
                    </Grid>
                }

                {
                    editGovernanceStatus && <Grid item xs={12} marginTop={4}>
                        <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' className='fontweight-600'>{translate('_changeGovernanceStatusTo')}</Typography>
                            </Grid>
                
                            {
                                Object.values(GovernanceStatusData)
                                    .filter(s => s.partnerId 
                                        && (
                                            s.partnerId === state.values.room?.governanceStatusId 
                                            || hasPermission(`Governance.RoomOverview.EditGovernanceStatus.${StringHelper.FirstLetterUpperCase(s.name)}`)
                                        )
                                    ).map(s => {
                                        const currentRoomGovernanceStatus = s.partnerId === state.values.room?.governanceStatusId;
                                        const disabled = currentRoomGovernanceStatus;
                
                                        return <Grid item sm={4} xs={6} key={s.key}>
                                            <FormOption
                                                name='governanceStatusId'
                                                type='radio'
                                                value={s.partnerId ?? ''}
                                                title={translate(s.name)}
                                                selected={currentRoomGovernanceStatus || s.partnerId === state.values.governanceStatusId}
                                                disabled={disabled}
                                                required={state.values.governanceStatusId === undefined}
                                                onClick={() => state.update('governanceStatusId', s.partnerId ?? '')}
                                                Icon={s.Icon}
                                                orientation='vertical'
                                                selectedProps={{
                                                    sx: {
                                                        backgroundColor: `${s.color} !important`
                                                    }
                                                }}
                                            />
                                        </Grid>;
                                    })
                            }
                        </Grid>

                        <Grid item xs={12} className="display-flex alignitems-center justifycontent-flexend" marginTop={4}>
                            <Badge badgeContent={state.values.attachments.length} showZero={true} className='backgroundcolor-primary color-white'>
                                <IconButton size="large" onClick={() => addAttachmentsDialog.handleOpen()} className='borderradius-default backgroundcolor-grey-2 text-color-1 display-flex gap-12'>
                                    <Typography className='fontsize-14'>{translate('addAttachments')}</Typography>
                                    <AttachFile fontSize="small" />
                                </IconButton>
                            </Badge>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField 
                                label={translate('observations')} 
                                name='observations' 
                                value={state.values.observations} 
                                required={false}
                                onChange={e => state.update('observations', e.target.value)}
                                multiline={true}
                                minRows={4}
                                maxRows={4}
                            />
                        </Grid>
                    </Grid>
                }

                {
                    governanceItems.length > 0 && (
                        <GovernanceItemsDialog
                            open={governanceItemsDialog.open}
                            handleClose={() => governanceItemsDialog.handleClose()}
                            governanceItems={governanceItems}
                            room={state.values.room}
                            hasPendingGovernanceItems={(value) => state.update('hasPendingGovernanceItems', value)}
                        />
                    )
                }

                <AddAttachmentsDialog
                    open={addAttachmentsDialog.open}
                    handleClose={() => addAttachmentsDialog.handleClose()}
                    attachments={state.values.attachments}
                    handleUpdateAttachments={(files) => state.update('attachments', files)}
                />
            </Grid>
        </Dialog>
    );
}

export default GovernanceDialog;